import { useState, useEffect, useCallback, useMemo } from 'react'
import { PageHeader, Card, Button, Badge, DataTable, TableColumn, Input, Select, useBestaetigung, useEinsatz } from '@pilot-hub/ui'
import { flightApi, droneApi, userApi, missionApi, akkuApi } from '@pilot-hub/api-client'
import { useAuth } from '@pilot-hub/auth'
import { ArrowLeft, Edit, Trash2, Save, Lock, LockOpen, MoreHorizontal, ChevronRight } from 'lucide-react'
import { EinsatzortDisplay } from '../components/EinsatzortDisplay'
import { GpsButton } from '../components/GpsButton'
import { FlugartWahl } from '../components/FlugartWahl'
import { FlugroutenKarte } from '../components/FlugroutenKarte'
import { FlugAufnahme } from '../components/FlugAufnahme'
import { akkuAnzeige } from '../utils/akku'
import { VorkommnisFelder, VorkommnisWert, vorkommnisAusApi, vorkommnisZuApi, vorkommnisFehler, vorkommnisBemerkungZuApi, useFlugHms } from '../components/VorkommnisFelder'

interface Flug {
  id: number
  flug_nr: string
  drohne_id: number
  drohne_label?: string
  einsatz_id?: number
  einsatz_titel?: string
  pilot_id: number
  pilot_label?: string
  co_pilot_id?: number
  co_pilot_label?: string
  beobachter_id?: number
  beobachter_label?: string
  akku_id?: number
  akku_label?: string
  startzeit: string
  endzeit?: string
  startort: string
  zielort?: string
  wetter?: string
  flugart?: string | null
  vorkommnis?: boolean | null
  vorkommnis_bemerkung?: string | null
  bemerkungen?: string
  dauer_minuten?: number
}

interface Drohne { id: number; label: string; asb_inventarnummer: string; hersteller: string; modell: string }
interface Nutzer { id: number; label: string; vorname?: string; nachname?: string; benutzername: string }
interface Einsatz { id: number; titel: string; typ: string; status: string }
interface Akku { id: number; label: string; bezeichnung: string; kapazitaet_mah: number; besitzer_label?: string | null }

const fmtDT = (s: string | null | undefined) => s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'

export function FlightLogPage() {
  const { user: currentUser } = useAuth()
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung()
  const [view, setView] = useState<{ mode: 'list' | 'edit' | 'detail'; flugId?: number }>({ mode: 'list' })
  const [fluege, setFluege] = useState<Flug[]>([])
  const [drohnen, setDrohnen] = useState<Drohne[]>([])
  const [piloten, setPiloten] = useState<Nutzer[]>([])
  const [coPiloten, setCoPiloten] = useState<Nutzer[]>([])
  const [luftraumbeobachter, setLuftraumbeobachter] = useState<Nutzer[]>([])
  const [einsaetze, setEinsaetze] = useState<Einsatz[]>([])
  const [akkus, setAkkus] = useState<Akku[]>([])
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [search, setSearch] = useState('')
  const [dateFilter, setDateFilter] = useState('all')
  const [missionFilter, setMissionFilter] = useState('all')
  const [pilotLocked, setPilotLocked] = useState(true)
  const [menueId, setMenueId] = useState<number | null>(null)
  const { einsatzId: globalEinsatzId } = useEinsatz()

  // Nur laufende Einsätze für Dropdown (LAUFEND, ALARMIERUNG, ABFAHRT_WACHE, ANKUNFT_EINSATZORT, EINSATZENDE)
  const laufendeEinsaetze = useMemo(() => einsaetze.filter(e => 
    e.status === 'LAUFEND' || 
    e.status === 'ALARMIERUNG' || 
    e.status === 'ABFAHRT_WACHE' || 
    e.status === 'ANKUNFT_EINSATZORT' ||
    e.status === 'EINSATZENDE'
  ), [einsaetze])

  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const mapU = (u: any) => ({ ...u, label: `${u.vorname ?? ''} ${u.nachname ?? ''} (${u.benutzername})` })
      const [f, d, p, cp, lb, e, a] = await Promise.all([
        flightApi.list({ pilot_id: currentUser?.id }).then(r => r.data.items),
        droneApi.getDropdownList().then(r => r.data),
        // Dropdowns nach FUNKTION filtern (nicht Rolle)
        userApi.list({ funktion: 'PILOT', size: 500 }).then(r => (r.data.items || r.data).map(mapU)),
        userApi.list({ funktion: 'CO_PILOT', size: 500 }).then(r => (r.data.items || r.data).map(mapU)),
        userApi.list({ funktion: 'LUFTRAUM_BEOBACHTER', size: 500 }).then(r => (r.data.items || r.data).map(mapU)),
        missionApi.getForFlight(),
        akkuApi.list().then(r => r.data.items as Akku[]),
      ])
      setFluege(f); setDrohnen(d); setPiloten(p); setCoPiloten(cp)
      setLuftraumbeobachter(lb); setEinsaetze(e.data); setAkkus(a)
    } catch (err: any) { setFehler(err?.response?.data?.detail || 'Laden fehlgeschlagen') }
    finally { setLoading(false) }
  }, [currentUser?.id])

  useEffect(() => { laden() }, [laden])

  const gefiltert = useMemo(() => fluege.filter(f => {
    const matchesSearch = search === '' ||
      f.flug_nr?.toLowerCase().includes(search.toLowerCase()) ||
      f.drohne_label?.toLowerCase().includes(search.toLowerCase()) ||
      f.pilot_label?.toLowerCase().includes(search.toLowerCase()) ||
      f.startort?.toLowerCase().includes(search.toLowerCase())
    const matchesDate = dateFilter === 'all' ||
      (/^\d{4}$/.test(dateFilter) && new Date(f.startzeit).getFullYear() === Number(dateFilter)) ||
      (dateFilter === 'today' && f.startzeit.startsWith(new Date().toISOString().split('T')[0])) ||
      (dateFilter === 'week' && new Date(f.startzeit) >= new Date(Date.now() - 7*86400000)) ||
      (dateFilter === 'month' && new Date(f.startzeit) >= new Date(Date.now() - 30*86400000))
    const matchesMission = missionFilter === 'all' || f.einsatz_id === Number(missionFilter)
    return matchesSearch && matchesDate && matchesMission
  }), [fluege, search, dateFilter, missionFilter])

  const jahre = useMemo(() => Array.from(new Set(fluege.map(f => new Date(f.startzeit).getFullYear()).filter(y => !isNaN(y)))).sort((a, b) => b - a), [fluege])

  const initialForm = {
    flug_nr: '',
    einsatz_id: null as number | null,
    drohne_id: '',
    pilot_id: '',
    co_pilot_id: null as number | null,
    beobachter_id: null as number | null,
    akku_id: null as number | null,
    startzeit: new Date().toISOString().slice(0, 16),
    endzeit: '',
    startort: '',
    zielort: '',
    wetter: '',
    flugart: '',
    vorkommnis: '' as VorkommnisWert,
    vorkommnis_bemerkung: '',
    bemerkungen: '',
  }

  const [form, setForm] = useState(initialForm)

  const mapToForm = (f: Flug) => ({
    flug_nr: f.flug_nr,
    einsatz_id: f.einsatz_id ?? null,
    drohne_id: String(f.drohne_id),
    pilot_id: String(f.pilot_id),
    co_pilot_id: f.co_pilot_id ?? null,
    beobachter_id: f.beobachter_id ?? null,
    akku_id: f.akku_id ?? null,
    startzeit: f.startzeit.slice(0, 16),
    endzeit: f.endzeit?.slice(0, 16) || '',
    startort: f.startort,
    zielort: f.zielort || '',
    wetter: f.wetter || '',
    flugart: f.flugart || '',
    vorkommnis: vorkommnisAusApi(f.vorkommnis),
    vorkommnis_bemerkung: f.vorkommnis_bemerkung || '',
    bemerkungen: f.bemerkungen || '',
  })

  async function speichern() {
    const vkFehler = vorkommnisFehler(form.vorkommnis, form.vorkommnis_bemerkung)
    if (vkFehler) { setFehler(vkFehler); return }
    setSpeichert(true); setFehler(null)
    try {
      const payload = {
        einsatz_id: form.einsatz_id,
        drohne_id: Number(form.drohne_id),
        pilot_id: Number(form.pilot_id),
        co_pilot_id: form.co_pilot_id,
        beobachter_id: form.beobachter_id,
        akku_id: form.akku_id,
        startzeit: form.startzeit + ':00',
        endzeit: form.endzeit ? form.endzeit + ':00' : null,
        startort: form.startort,
        zielort: form.zielort || null,
        wetter: form.wetter || null,
        flugart: form.flugart || null,
        vorkommnis: vorkommnisZuApi(form.vorkommnis),
        vorkommnis_bemerkung: vorkommnisBemerkungZuApi(form.vorkommnis, form.vorkommnis_bemerkung),
        bemerkungen: form.bemerkungen || null,
      }
      await flightApi.update(view.flugId!, payload)
      zurueckZurListe(); laden()
    } catch (err: any) { setFehler(err?.response?.data?.detail || 'Speichern fehlgeschlagen') }
    finally { setSpeichert(false) }
  }

  // „⋯“-Menü (≥ 48 px): Bearbeiten oben, Löschen räumlich getrennt darunter (Bestätigung in loescheFlug)
  const aktionsMenue = (f: Flug) => (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button type="button" onClick={() => setMenueId(menueId === f.id ? null : f.id)} aria-haspopup="menu" aria-expanded={menueId === f.id}
        className="flex h-12 w-12 items-center justify-center rounded-xl text-dark-text-muted transition-colors hover:bg-dark-bg-tertiary hover:text-dark-text" title="Aktionen" aria-label={`Aktionen für ${f.flug_nr}`}>
        <MoreHorizontal className="h-6 w-6" />
      </button>
      {menueId === f.id && (
        <>
          <button type="button" aria-label="Menü schließen" className="fixed inset-0 z-30 cursor-default" onClick={() => setMenueId(null)} />
          <div role="menu" className="absolute right-0 top-full z-40 mt-1 w-56 overflow-hidden rounded-xl border border-dark-border bg-dark-bg-secondary shadow-xl">
            <button type="button" role="menuitem" onClick={() => { setMenueId(null); oeffneBearbeiten(f) }} className="flex min-h-[52px] w-full items-center gap-3 px-4 text-left text-base text-dark-text hover:bg-dark-bg-tertiary"><Edit className="h-5 w-5" />Bearbeiten</button>
            <div className="h-3 border-t border-dark-border bg-dark-bg-tertiary/40" />
            <button type="button" role="menuitem" onClick={() => { setMenueId(null); void loescheFlug(f) }} className="flex min-h-[52px] w-full items-center gap-3 px-4 text-left text-base text-red-300 hover:bg-red-500/10"><Trash2 className="h-5 w-5" />Löschen…</button>
          </div>
        </>
      )}
    </div>
  )

  // Columns defined inside component for access to functions
  const columns: TableColumn[] = [
    { key: 'flug_nr', header: 'Flug-Nr.', accessor: 'flug_nr', render: (_v: any, f: any) => <span className="font-mono font-medium">{f.flug_nr}</span> },
    { key: 'startzeit', header: 'Start', accessor: 'startzeit', render: (_v: any, f: any) => fmtDT(f.startzeit) },
    { key: 'endzeit', header: 'Ende', accessor: 'endzeit', render: (_v: any, f: any) => fmtDT(f.endzeit) },
    { key: 'drohne_label', header: 'Drohne', accessor: 'drohne_label', render: (_v: any, f: any) => f.drohne_label || '—' },
    { key: 'einsatz_titel', header: 'Einsatz', accessor: 'einsatz_titel', render: (_v: any, f: any) => f.einsatz_titel ? <Badge variant="info">{f.einsatz_titel}</Badge> : <span className="text-dark-text-subtle">—</span> },
    { key: 'pilot_label', header: 'Pilot', accessor: 'pilot_label', render: (_v: any, f: any) => f.pilot_label || '—' },
    { key: 'startort', header: 'Startort', accessor: 'startort', render: (_v: any, f: any) => <EinsatzortDisplay value={f.startort} /> },
    { key: 'status', header: 'Status', accessor: 'endzeit', render: (_v: any, f: any) => f.endzeit ? <Badge variant="success">Abgeschlossen</Badge> : <Badge variant="info">Läuft</Badge> },
    { key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, f: any) => aktionsMenue(f) },
  ]

  const loescheFlug = async (f: Flug): Promise<boolean> => {
    const ok = await bestaetigen({
      titel: 'Flug löschen?',
      text: 'Flug',
      objekt: `${fmtDT(f.startzeit)}${f.drohne_label ? ` · ${f.drohne_label}` : ''}`,
      hinweis: 'Telemetrie, Akku-Flugdaten und Steuerungsprotokoll dieses Flugs werden mitgelöscht. Videos, Medien und Aufnahmen bleiben erhalten (ohne Flugzuordnung). Das Löschen kann nicht rückgängig gemacht werden.',
      bestaetigenText: 'Flug löschen',
    })
    if (!ok) return false
    try { await flightApi.delete(f.id); await laden(); return true } catch (err: any) { setFehler(err?.response?.data?.detail || 'Flug konnte nicht gelöscht werden'); return false }
  }

  const currentFlug = view.flugId ? fluege.find(f => f.id === view.flugId) : null
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'
  // Meldungen der Drohne (DJI HMS) im Flug: Hinweis und Vorschlag „Vorkommnis“ bei den Vorkommnissen
  const flugHms = useFlugHms(view.mode === 'edit' || view.mode === 'detail' ? view.flugId : null)

  const oeffneBearbeiten = (f: Flug) => { setView({ mode: 'edit', flugId: f.id }); setForm(mapToForm(f)) }
  const oeffneDetail = (f: Flug) => { setView({ mode: 'detail', flugId: f.id }); setForm(mapToForm(f)) }
  const zurueckZurListe = () => { setView({ mode: 'list' }); setForm(initialForm) }

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        {bestaetigungsDialog}
        <PageHeader
          title=""
          subtitle="Flugbuch der ASB Drohnenstaffel — alle erfassten Flüge"
        />
        {fehler && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2"><span className="text-red-400">!</span>{fehler}</div>}

        {/* Statistics (folgen den Filtern) */}
        {(() => {
          const filterAktiv = gefiltert.length !== fluege.length || search !== '' || dateFilter !== 'all' || missionFilter !== 'all'
          const hinweis = filterAktiv ? <span className="ml-1.5 text-xs font-normal text-dark-text-subtle">gefiltert</span> : null
          return (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Card className="p-3 sm:p-4"><p className="text-sm text-dark-text-muted">Flüge gesamt{hinweis}</p><p className="mt-1 text-2xl font-bold sm:text-3xl text-dark-text">{gefiltert.length}{filterAktiv && <span className="ml-2 text-sm font-normal text-dark-text-subtle">von {fluege.length}</span>}</p></Card>
              <Card className="p-3 sm:p-4"><p className="text-sm text-dark-text-muted">Flugstunden gesamt{hinweis}</p><p className="mt-1 text-2xl font-bold sm:text-3xl text-dark-text">{(gefiltert.reduce((sum, f) => sum + (f.dauer_minuten || 0), 0) / 60).toFixed(1)} h{filterAktiv && <span className="ml-2 text-sm font-normal text-dark-text-subtle">von {(fluege.reduce((sum, f) => sum + (f.dauer_minuten || 0), 0) / 60).toFixed(1)} h</span>}</p></Card>
              <Card className="p-3 sm:p-4"><p className="text-sm text-dark-text-muted">Aktive Flüge{hinweis}</p><p className="mt-1 text-2xl font-bold sm:text-3xl text-blue-400">{gefiltert.filter(f => !f.endzeit).length}{filterAktiv && <span className="ml-2 text-sm font-normal text-dark-text-subtle">von {fluege.filter(f => !f.endzeit).length}</span>}</p></Card>
              <Card className="p-3 sm:p-4"><p className="text-sm text-dark-text-muted">Abgeschlossen{hinweis}</p><p className="mt-1 text-2xl font-bold sm:text-3xl text-green-400">{gefiltert.filter(f => f.endzeit).length}{filterAktiv && <span className="ml-2 text-sm font-normal text-dark-text-subtle">von {fluege.filter(f => f.endzeit).length}</span>}</p></Card>
            </div>
          )
        })()}

        {/* Schnellfilter */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Schnellfilter">
          {([
            { key: 'meine', label: 'Meine Flüge', aktiv: missionFilter === 'all', onClick: () => setMissionFilter('all'), disabled: false },
            { key: 'einsatz', label: 'Dieser Einsatz', aktiv: globalEinsatzId != null && missionFilter === String(globalEinsatzId), onClick: () => globalEinsatzId != null && setMissionFilter(String(globalEinsatzId)), disabled: globalEinsatzId == null },
          ]).map(c => (
            <button key={c.key} type="button" disabled={c.disabled} aria-pressed={c.aktiv} onClick={c.onClick}
              title={c.disabled ? 'Kein Einsatz gewählt' : undefined}
              className={`min-h-[48px] rounded-full border px-5 text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${c.aktiv ? 'border-[rgb(125_211_252/0.7)] bg-[rgb(125_211_252/0.18)] text-[rgb(125_211_252)]' : 'border-dark-border bg-dark-bg-tertiary text-dark-text-muted'}`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-center sm:justify-between">
            <Input placeholder="Nach Flug-Nr., Drohne, Pilot, Ort suchen..." value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-72" />
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <div className="w-full sm:w-44 sm:shrink-0"><Select value={dateFilter} onChange={e => setDateFilter(e.target.value)} placeholder="Zeitraum"
                  options={[{ value: 'all', label: 'Alle Zeiträume' }, { value: 'today', label: 'Heute' }, { value: 'week', label: 'Diese Woche' }, { value: 'month', label: 'Dieser Monat' }, ...jahre.map(j => ({ value: String(j), label: `Jahr ${j}` }))]} /></div>
              <div className="w-full sm:w-56 sm:shrink-0"><Select value={missionFilter} onChange={e => setMissionFilter(e.target.value)} placeholder="Einsatz"
                  options={[{ value: 'all', label: 'Alle Einsätze' }, ...einsaetze.map(e => ({ value: String(e.id), label: e.titel }))]} /></div>
            </div>
            <div className="text-sm text-dark-text-muted whitespace-nowrap">{gefiltert.length} von {fluege.length}</div>
          </div>
        </Card>

        {/* Handy: Kartenliste, Tippen öffnet das Detail */}
        <div className="space-y-2 md:hidden">
          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Flüge...</div>
            : gefiltert.length === 0 ? <div className="py-8 text-center text-dark-text-muted">Keine Flüge vorhanden</div>
            : gefiltert.map(f => (
              <div key={f.id} className="flex items-stretch rounded-2xl border border-dark-border bg-dark-bg-secondary/70">
                <button type="button" onClick={() => oeffneDetail(f)} className="flex min-h-[72px] min-w-0 flex-1 items-center gap-3 rounded-l-2xl px-4 py-3 text-left">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-base font-semibold text-dark-text">{f.flug_nr}</span>
                      {f.endzeit ? <Badge variant="success">Abgeschlossen</Badge> : <Badge variant="info">Läuft</Badge>}
                    </div>
                    <div className="mt-0.5 text-sm text-dark-text-muted">{fmtDT(f.startzeit)}</div>
                    <div className="truncate text-sm text-dark-text-muted">{f.drohne_label || '—'}</div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-dark-text-subtle" />
                </button>
                <div className="flex items-center pr-1">{aktionsMenue(f)}</div>
              </div>
            ))}
        </div>

        <Card className="hidden md:block">
          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Flüge...</div> : (
            <DataTable data={gefiltert} columns={columns} onRowClick={oeffneDetail} keyAccessor={(f: any) => String(f.id)} emptyMessage="Keine Flüge vorhanden" />
          )}
        </Card>
      </div>
    )
  }

  // ====== DETAIL / EDIT VIEW ======
  return (
    <div className="space-y-6">
      {bestaetigungsDialog}
      <PageHeader
        title={isEdit ? 'Flug bearbeiten' : 'Flug anzeigen'}
        subtitle={isEdit ? 'Flug bearbeiten' : currentFlug ? currentFlug.flug_nr : ''}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentFlug && <Button onClick={() => oeffneBearbeiten(currentFlug)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
            {!isDetail && <Button onClick={speichern} disabled={speichert || !form.drohne_id || !form.pilot_id || !form.startzeit || !form.startort}><Save className="h-4 w-4 mr-1" />{speichert ? 'Speichern…' : 'Speichern'}</Button>}
          </div>
        }
      />

      <Card>
        <form onSubmit={e => { e.preventDefault(); speichern() }} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Flug-Nr. *</label>
              <Input value={form.flug_nr} disabled placeholder="Wird automatisch generiert" className="mt-1.5" />
            </div>
            <div>
              <label className="label">Einsatz</label>
              <Select value={form.einsatz_id ?? ''} onChange={e => setForm({...form, einsatz_id: e.target.value ? Number(e.target.value) : null})} disabled={isDetail}
                  options={[{ value: '', label: '-- Kein Einsatz --' }, ...laufendeEinsaetze.map(e => ({ value: String(e.id), label: `${e.titel} (${e.typ})` }))]} className="mt-1.5" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="label">Drohne *</label>
              <Select value={form.drohne_id} onChange={e => setForm({...form, drohne_id: e.target.value})} disabled={isDetail}
                  options={drohnen.map(d => ({ value: String(d.id), label: d.label || d.asb_inventarnummer }))} placeholder="Drohne wählen" className="mt-1.5" />
            </div>
            <div>
              <label className="label flex items-center gap-2">
                Pilot *
                <button
                  type="button"
                  onClick={() => setPilotLocked(!pilotLocked)}
                  className="p-1 rounded hover:bg-dark-bg-tertiary transition-colors"
                  title={pilotLocked ? 'Pilot-Feld entsperren' : 'Pilot-Feld sperren'}
                >
                  {pilotLocked ? <Lock className="h-4 w-4 text-primary-600" /> : <LockOpen className="h-4 w-4 text-green-600" />}
                </button>
              </label>
              <Select value={form.pilot_id} onChange={e => setForm({...form, pilot_id: e.target.value})} disabled={isDetail || pilotLocked}
                  options={piloten.map(p => ({ value: String(p.id), label: p.label }))} placeholder="Pilot wählen" className="mt-1.5" />
            </div>
            <div>
              <label className="label">Co-Pilot</label>
              <Select value={form.co_pilot_id ?? ''} onChange={e => setForm({...form, co_pilot_id: e.target.value ? Number(e.target.value) : null})} disabled={isDetail}
                  options={[{ value: '', label: '-- Kein Co-Pilot --' }, ...coPiloten.map(p => ({ value: String(p.id), label: p.label }))]} className="mt-1.5" />
            </div>
            <div>
              <label className="label">Luftraumbeobachter</label>
              <Select value={form.beobachter_id ?? ''} onChange={e => setForm({...form, beobachter_id: e.target.value ? Number(e.target.value) : null})} disabled={isDetail}
                  options={[{ value: '', label: '-- Kein Luftraumbeobachter --' }, ...luftraumbeobachter.map(b => ({ value: String(b.id), label: b.label }))]} className="mt-1.5" />
            </div>
          </div>

          <div>
            <label className="label">Akku</label>
            <Select value={form.akku_id ?? ''} onChange={e => setForm({...form, akku_id: e.target.value ? Number(e.target.value) : null})} disabled={isDetail}
                options={[{ value: '', label: '-- Kein Akku --' }, ...akkus.map(a => ({ value: String(a.id), label: a.kapazitaet_mah ? `${akkuAnzeige(a)} · ${a.kapazitaet_mah} mAh` : akkuAnzeige(a) }))]} className="mt-1.5" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Startzeit *</label>
              <Input type="datetime-local" value={form.startzeit} onChange={e => setForm({...form, startzeit: e.target.value})} disabled={isDetail} className="mt-1.5" />
            </div>
            <div>
              <label className="label">Endzeit</label>
              <Input type="datetime-local" value={form.endzeit} onChange={e => setForm({...form, endzeit: e.target.value})} disabled={isDetail} className="mt-1.5" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Startort *</label>
              <div className="flex items-start gap-2">
                <Input value={form.startort} onChange={e => setForm({...form, startort: e.target.value})} disabled={isDetail} placeholder="z.B. Einsatzort, Koordinaten" className="mt-1.5 flex-1" />
                {!isDetail && <GpsButton onCoords={v => setForm({ ...form, startort: v })} />}
              </div>
              {isDetail && <p className="mt-1 text-xs text-dark-text-muted"><EinsatzortDisplay value={form.startort} /></p>}
            </div>
            <div>
              <label className="label">Zielort</label>
              <div className="flex items-start gap-2">
                <Input value={form.zielort} onChange={e => setForm({...form, zielort: e.target.value})} disabled={isDetail} placeholder="z.B. Landeplatz, Koordinaten" className="mt-1.5 flex-1" />
                {!isDetail && <GpsButton onCoords={v => setForm({ ...form, zielort: v })} title="Aktuelle GPS-Koordinaten als Landeort übernehmen" />}
              </div>
              {isDetail && <p className="mt-1 text-xs text-dark-text-muted"><EinsatzortDisplay value={form.zielort} /></p>}
            </div>
          </div>

          <div>
            <label className="label">Flugart</label>
            <FlugartWahl value={form.flugart} onChange={v => setForm({...form, flugart: v})} disabled={isDetail} />
          </div>

          <div>
            <label className="label">Wetter</label>
            <Input value={form.wetter} onChange={e => setForm({...form, wetter: e.target.value})} disabled={isDetail} placeholder="z.B. Sonnig, 15°C, Wind 5km/h NO" className="mt-1.5" />
          </div>

          <div>
            <label className="label">Bemerkungen</label>
            <textarea className="input min-h-[100px] resize-y mt-1.5" value={form.bemerkungen} onChange={e => setForm({...form, bemerkungen: e.target.value})} disabled={isDetail} placeholder="Besonderheiten etc." />
          </div>

          <VorkommnisFelder wert={form.vorkommnis} bemerkung={form.vorkommnis_bemerkung} disabled={isDetail} hms={flugHms}
            onChange={(vorkommnis, vorkommnis_bemerkung) => setForm({ ...form, vorkommnis, vorkommnis_bemerkung })} />

          {!isDetail && <div className="flex gap-4 pt-4 border-t border-dark-border"><Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-2" />Abbrechen</Button><Button type="submit" disabled={speichert || !form.drohne_id || !form.pilot_id || !form.startzeit || !form.startort}><Save className="h-4 w-4 mr-2" />{speichert ? 'Speichern…' : 'Speichern'}</Button></div>}
        </form>
      </Card>

      {isDetail && currentFlug && (
        <Card>
          <FlugroutenKarte flug={currentFlug} />
        </Card>
      )}

      {isDetail && currentFlug && (
        <Card>
          <FlugAufnahme flug={currentFlug} />
        </Card>
      )}

      {/* Löschen bewusst unten und abgesetzt (mit Bestätigung) */}
      {isDetail && currentFlug && (
        <div className="mt-8 border-t border-dark-border pt-6">
          <Button variant="danger" onClick={async () => { if (await loescheFlug(currentFlug)) zurueckZurListe() }} className="min-h-[48px]"><Trash2 className="mr-2 h-4 w-4" />Flug löschen…</Button>
        </div>
      )}
    </div>
  )
}